import { useState, useEffect, useCallback } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { useWizard } from '../../context/WizardContext';
import ImageUploader from './inputs/ImageUploader';
import ScaleCalibrator from './inputs/ScaleCalibrator';

/**
 * Step2BackgroundImage Component
 *
 * Wizard Step 2: Upload and configure background image (optional)
 * Design based on: docs/design/screenshots/05_Dialog_Hintergrundbild_Import_*.png
 *
 * Features:
 * - Drag-and-drop image upload
 * - File validation (PNG, JPG, max 10MB)
 * - Interactive 2-point scale calibration with Fabric.js
 * - Image drag and rotation (Fabric.js built-in controls)
 * - Transparency control
 * - Optional step (can be skipped)
 */
const Step2BackgroundImage = () => {
  const { gardenData, updateBackgroundImage } = useWizard();
  const [localFile, setLocalFile] = useState(gardenData.backgroundImage?.file || null);
  const [localDataUrl, setLocalDataUrl] = useState(gardenData.backgroundImage?.dataUrl || null);
  const [error, setError] = useState(null);
  const [calibration, setCalibration] = useState(
    gardenData.backgroundImage?.calibration || {
      point1: null,
      point2: null,
      pixelDistance: null,
      realDistance: 5,
      scale: null,
      isCalibrated: false,
    }
  );
  const [transparency, setTransparency] = useState(gardenData.backgroundImage?.transparency ?? 50);

  /**
   * Handle file selection
   */
  const handleFileSelect = (file, dataUrl, validationError) => {
    if (validationError) {
      setError(validationError);
      setLocalFile(null);
      setLocalDataUrl(null);
      return;
    }

    setError(null);
    setLocalFile(file);
    setLocalDataUrl(dataUrl);
  };

  /**
   * Handle file removal
   */
  const handleFileRemove = () => {
    setLocalFile(null);
    setLocalDataUrl(null);
    setError(null);
  };

  /**
   * Handle calibration changes from ScaleCalibrator
   */
  const handleCalibrationChange = (newCalibration) => {
    setCalibration(newCalibration);
  };

  /**
   * Handle real distance change - recalculate scale if calibrated.
   * nativePixelsPerMeter scales inversely with realDistance (image fit factor
   * is unchanged), so we can rescale it without re-reading the canvas.
   */
  const handleRealDistanceChange = (e) => {
    const value = parseFloat(e.target.value);
    if (!isNaN(value) && value > 0) {
      const newCalibration = {
        ...calibration,
        realDistance: value,
      };
      if (newCalibration.pixelDistance) {
        newCalibration.scale = newCalibration.pixelDistance / value;
        if (calibration.realDistance && calibration.nativePixelsPerMeter) {
          newCalibration.nativePixelsPerMeter =
            calibration.nativePixelsPerMeter * (calibration.realDistance / value);
        }
      }
      setCalibration(newCalibration);
    }
  };

  /**
   * Handle reset calibration
   */
  const handleResetCalibration = () => {
    setCalibration({
      point1: null,
      point2: null,
      pixelDistance: null,
      realDistance: calibration.realDistance, // keep distance
      scale: null,
      isCalibrated: false,
    });
  };

  /**
   * Handle image transformation (position, rotation)
   */
  const handleImageTransform = useCallback((transform) => {
    updateBackgroundImage({
      ...gardenData.backgroundImage,
      position: transform.position,
      rotation: transform.rotation,
    });
  }, [gardenData.backgroundImage, updateBackgroundImage]);

  /**
   * Handle transparency change
   */
  const handleTransparencyChange = (e) => {
    setTransparency(parseInt(e.target.value));
  };

  /**
   * Calculate transparency percentage for slider gradient
   */
  const transparencyPercentage = (transparency / 100) * 100;

  /**
   * Save to wizard context whenever values change
   */
  useEffect(() => {
    updateBackgroundImage({
      file: localFile,
      dataUrl: localDataUrl,
      calibration,
      transparency,
      position: gardenData.backgroundImage?.position || { x: 20, y: 20 },
      rotation: gardenData.backgroundImage?.rotation || 0,
      locked: gardenData.backgroundImage?.locked || false,
    });
  }, [localFile, localDataUrl, calibration, transparency, gardenData.backgroundImage?.locked, gardenData.backgroundImage?.position, gardenData.backgroundImage?.rotation, updateBackgroundImage]);

  return (
    <div className="w-full mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <ImageIcon size={20} className="text-gray-700" />
        <h2 className="text-xl font-bold text-gray-900">Hintergrundbild (Optional)</h2>
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600">
        Laden Sie ein Foto, einen Grundriss oder ein Satellitenbild Ihres Gartens hoch,
        um präzise planen zu können. Dieser Schritt ist optional.
      </p>

      {/* Image Uploader */}
      <ImageUploader
        file={localFile}
        dataUrl={localDataUrl}
        onFileSelect={handleFileSelect}
        onFileRemove={handleFileRemove}
        error={error}
      />

      {/* Calibration Section - Only show when image is uploaded */}
      {localFile && localDataUrl && (
        <div className="space-y-4">
          {/* Interactive Scale Calibrator with Fabric.js */}
          <ScaleCalibrator
            dataUrl={localDataUrl}
            calibration={calibration}
            transparency={transparency}
            onCalibrationChange={handleCalibrationChange}
            onReset={handleResetCalibration}
            onImageTransform={handleImageTransform}
          />

          {/* Calibration Controls in Gray Box */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
            {/* Section Header */}
            <div className="flex items-center gap-2 mb-3">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                className="text-gray-700"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                />
              </svg>
              <h3 className="text-base font-bold text-gray-900">Kalibrierung</h3>
            </div>

            {/* Real Distance Input */}
            <div className="flex items-center justify-between">
              <label htmlFor="realDistance" className="text-sm font-medium text-gray-700 w-40">
                Reale Distanz:
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  id="realDistance"
                  value={calibration.realDistance}
                  onChange={handleRealDistanceChange}
                  min="0.1"
                  max="100"
                  step="0.1"
                  className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-md
                             focus:outline-none focus:ring-1 focus:ring-emerald-500"
                  data-testid="real-distance-input"
                />
                <span className="text-sm text-gray-700">m</span>
              </div>
            </div>

            {/* Calculated Scale */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 w-40">Maßstab:</span>
              <span
                className={`text-sm font-bold ${
                  calibration.isCalibrated ? 'text-emerald-600' : 'text-gray-400'
                }`}
                data-testid="scale-display"
              >
                {calibration.scale
                  ? `1:${Math.round(calibration.scale)}`
                  : '(Bitte kalibrieren)'}
              </span>
            </div>

            {/* Transparency Slider */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="transparency" className="text-sm font-medium text-gray-700">
                  Transparenz
                </label>
                <span className="text-sm text-gray-600" data-testid="transparency-display">
                  {transparency} %
                </span>
              </div>
              <input
                type="range"
                id="transparency"
                min="0"
                max="100"
                step="1"
                value={transparency}
                onChange={handleTransparencyChange}
                className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #10b981 0%, #10b981 ${transparencyPercentage}%, #e5e7eb ${transparencyPercentage}%, #e5e7eb 100%)`,
                }}
                data-testid="transparency-slider"
              />
              {/* Transparency Scale Labels */}
              <div className="flex justify-between text-xs text-gray-400">
                <span>0 %</span>
                <span>25 %</span>
                <span>50 %</span>
                <span>75 %</span>
                <span>100 %</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Step2BackgroundImage;
